import React, { useEffect, useRef } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { XIcon } from 'lucide-react';
import { headerNavLeft, headerNavRight } from '../data/content';
import { contact } from '../data/contact';
import { useLanguage } from '../contexts/LanguageContext';
type MenuDrawerProps = {
  open: boolean;
  onClose: () => void;
};
const ease = [0.23, 1, 0.32, 1] as const;
export function MenuDrawer({
  open,
  onClose
}: MenuDrawerProps) {
  const closeRef = useRef<HTMLButtonElement>(null);
  const reduceMotion = useReducedMotion();
  const {
    t,
    locale
  } = useLanguage();
  // The drawer opens from the side the Menu button sits on
  const from = locale === 'ar' ? '100%' : '-100%';
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    closeRef.current?.focus();
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);
  return <AnimatePresence data-id="element-683" data-mp="1dpipxt7kqria">
      {open && <div className="fixed inset-0 z-50" role="dialog" aria-modal="true" aria-label={t('nav.menu')} data-id="element-684" data-mp="1iolttabtiroo">
          <motion.div className="absolute inset-0 bg-ink/30" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.5,
        ease
      }} onClick={onClose} data-id="element-685" data-mp="1nfkdmjw0m7wo" />
          <motion.aside className="absolute start-0 top-0 flex h-full w-full max-w-[460px] flex-col bg-paper" initial={reduceMotion ? {
        opacity: 0
      } : {
        x: from
      }} animate={reduceMotion ? {
        opacity: 1
      } : {
        x: 0
      }} exit={reduceMotion ? {
        opacity: 0
      } : {
        x: from
      }} transition={{
        duration: 0.7,
        ease
      }} data-id="element-686" data-mp="1whjxrl2bts3u">
            <div className="flex items-center justify-between border-b border-line px-6 py-5 md:px-10" data-id="element-687" data-mp="c05illnd9q47">
              <span className="text-label font-bold uppercase" data-id="element-688" data-mp="3zfz7eavh29d">{t('nav.menu')}</span>
              <button ref={closeRef} type="button" onClick={onClose} aria-label={t('nav.closeMenu')} className="transition-opacity duration-150 hover:opacity-60" data-id="element-689" data-mp="3v71x0qawuaib">
                <XIcon className="h-4 w-4" strokeWidth={1.25} data-id="element-690" data-mp="mxjv371r14t8" />
              </button>
            </div>

            <nav aria-label="Main" className="flex-1 overflow-y-auto px-6 py-10 md:px-10" data-id="element-691" data-mp="hziauisl6zg6">
              <ul className="space-y-5" data-id="element-692" data-mp="woeiwfun8cgs">
                {headerNavLeft.map(item => <li key={item.label} data-id="element-693" data-mp="rp1y00nc1fgu">
                    <a href={item.href} onClick={onClose} className="text-title font-bold uppercase transition-opacity duration-150 hover:opacity-60" data-id="element-694" data-mp="rq37i0op9twg">
                      {t(item.key)}
                    </a>
                  </li>)}
              </ul>
              <ul className="mt-12 space-y-4 border-t border-line pt-8" data-id="element-695" data-mp="1pa369n4l30ia">
                {headerNavRight.map(item => <li key={item.label} data-id="element-696" data-mp="1bbd781y7eqra">
                    <a href={item.href} onClick={onClose} className="text-body text-ink transition-opacity duration-150 hover:opacity-60" data-id="element-697" data-mp="1baaf5qp5ai9s">
                      {t(item.key)}
                    </a>
                  </li>)}
              </ul>
            </nav>

            <div className="border-t border-line bg-bone px-6 py-6 md:px-10" data-id="element-698" data-mp="sl3wu4s9jsoi">
              <p className="text-micro font-bold uppercase text-ink/40" data-id="element-699" data-mp="wl5ywf1wuo4b">{t('menu.clientLine')}</p>
              <a href={contact.phoneHref} className="mt-1 block text-small" dir="ltr" data-id="element-700" data-mp="ild0ryo215mb">
                {contact.phoneDisplay}
              </a>
              <a href={`mailto:${contact.email}`} className="mt-1 block text-small" data-id="element-701" data-mp="nkg9bt38ttz1">
                {contact.email}
              </a>
              <p className="mt-1 text-small text-ink/60" data-id="element-702" data-mp="1lwsh9jyicw24">{t('menu.clientLineNote')}</p>
            </div>
          </motion.aside>
        </div>}
    </AnimatePresence>;
}